import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Stack, Text } from '@mantine/core';
import { IconArrowBackUp, IconBan, IconX } from '@tabler/icons-react';
import { UseMutationResult } from '@tanstack/react-query';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form, TextareaControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { TravelDecisionRequest } from '../model/TravelApprovalTypes';

export type TravelApprovalReasonAction = 'return' | 'reject' | 'cancel';

interface TravelApprovalReasonModalProps {
  action: TravelApprovalReasonAction;
  title: string;
  content: string;
  submitLabel?: string;
  mutation: UseMutationResult<unknown, Error, TravelDecisionRequest>;
  onClose: () => void;
}

const schema = z.object({
  reason: z.string().trim().min(1),
});

const decisionReason = 'travel.approvals.actions.reason';

const actionPresets = {
  return: {
    label: 'travel.approvals.actions.return',
    reason: decisionReason,
    color: 'yellow.4',
    Icon: IconArrowBackUp,
  },
  reject: { label: 'travel.approvals.actions.reject', reason: decisionReason, color: 'red', Icon: IconBan },
  cancel: {
    label: 'travel.requests.actions.cancel',
    reason: 'travel.requests.actions.cancelReason',
    color: 'red',
    Icon: IconX,
  },
} as const;

export function TravelApprovalReasonModal({
  action,
  title,
  content,
  submitLabel,
  mutation,
  onClose,
}: TravelApprovalReasonModalProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { label, reason, color, Icon } = actionPresets[action];

  const form = useForm<TravelDecisionRequest>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  // Two clicks in one frame both see isPending false, so the loading button alone lets the second one through.
  const inFlight = useRef(false);

  const handleSubmit = (values: TravelDecisionRequest) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    mutation.mutate(values, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        onClose();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={title}
      size="lg"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={onClose}
        isSubmitting={mutation.isPending}
        submitLabel={submitLabel ?? t(label)}
        submitColor={color}
        submitIcon={<Icon />}
      >
        <Stack gap="md">
          <Text size="sm">{content}</Text>
          <TextareaControl
            name="reason"
            label={t(reason)}
            isSubmitting={mutation.isPending}
            withAsterisk
            control={form.control}
          />
        </Stack>
      </Form>
    </Modal>
  );
}
